import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, CheckCircle2, KeyRound, Lock, Plus, Trash2, XCircle } from "lucide-react";
import {
  StatusPill,
  ToolBody,
  ToolHeader,
  ToolPage,
  primaryButtonClass,
  secondaryButtonClass,
} from "./ToolPageChrome";

export default function ApiAccess({ user }) {
  const [apiKey, setApiKey] = useState("");
  const [apiKeysList, setApiKeysList] = useState([]);
  const [activeTab, setActiveTab] = useState("keys");
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const canUseApiAccess = user?.plan === "pro" || user?.plan === "pilot" || user?.plan === "enterprise";
  const isEnterprise = user?.plan === "enterprise";

  /* ---------------- CREATE API KEY ---------------- */
  const createApiKey = async () => {
    if (!canUseApiAccess) {
      alert("API Access is available as a Pro trial or Enterprise feature.");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/api-keys/create`,
        {
          method: "POST",
          credentials: "include",
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create API key");

      setApiKey(data.api_key);
      fetchApiKeys(); // refresh list
    } catch (err) {
      console.error(err);
      alert("Error: " + err.message);
    }
  };

  /* ---------------- FETCH API KEYS ---------------- */
  const fetchApiKeys = async () => {
    if (!canUseApiAccess) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/api-keys`,
        {
          credentials: "include",
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to fetch API keys");
      setApiKeysList(data);
    } catch (err) {
      console.error(err);
      alert("Error fetching API keys: " + err.message);
    }
  };

  /* ---------------- FETCH API HISTORY ---------------- */
  const fetchApiHistory = async () => {
    if (!canUseApiAccess) return;

    try {
      setLoadingHistory(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/api-keys/history`,
        {
          credentials: "include",
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to fetch API history");
      setHistory(data);
    } catch (err) {
      console.error(err);
      alert("Error fetching API history: " + err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  /* ---------------- DELETE API KEY ---------------- */
  const deleteApiKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to delete this API key?")) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/api-keys/${keyId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to delete API key");
      setApiKeysList((prev) => prev.filter((k) => k.id !== keyId));
    } catch (err) {
      console.error(err);
      alert("Error deleting API key: " + err.message);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [canUseApiAccess]);

  useEffect(() => {
    if (activeTab === "history" && !history) {
      fetchApiHistory();
    }
  }, [activeTab]);

  const summary = history?.summary || {};
  const rateLimits = summary.rate_limits || {};
  const recentCalls = history?.recent_calls || [];

  return (
    <motion.div className="w-full">
      <ToolPage>
        <ToolHeader
          icon={KeyRound}
          title="API Access"
          description="Create and manage API keys for programmatic CV analysis from your own tools."
          meta={<StatusPill tone="blue">{apiKeysList.length} active keys</StatusPill>}
        />
        <ToolBody className="space-y-6">
      {!canUseApiAccess && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <div className="flex items-start gap-3">
            <Lock size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-amber-950">API Access is available on Pro trial and Enterprise</p>
              <p className="mt-1">
                Preview the API key and usage-history workflow here. Pro includes 25 API trial calls; Enterprise unlocks production API access.
              </p>
            </div>
          </div>
        </div>
      )}
      {canUseApiAccess && !isEnterprise && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          Pro includes 25 API trial calls for testing. Upgrade to Enterprise for production API access and custom limits.
        </div>
      )}
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
        {[
          { key: "keys", label: "API Keys", icon: KeyRound },
          { key: "history", label: "History", icon: Activity },
        ].map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition ${
                selected
                  ? "bg-white text-slate-950 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "keys" && (
        <div className="space-y-6">
          <button
            onClick={createApiKey}
            disabled={!canUseApiAccess}
            className={primaryButtonClass}
          >
            <Plus size={16} />
            Generate API Key
          </button>

          {/* Newly created key */}
          {apiKey && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="font-semibold text-emerald-900">New API Key</p>
              <p className="mt-2 rounded-md bg-white border border-emerald-200 p-3 font-mono text-sm break-all text-slate-800">{apiKey}</p>
              <p className="mt-2 text-sm text-emerald-800">Copy this key now. For security, it will be masked in the list after this.</p>
            </div>
          )}

          {/* Existing keys */}
          {apiKeysList.length > 0 ? (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Existing API Keys</h3>
              <div className="space-y-2">
              {apiKeysList.map((key) => (
                <div
                  key={key.id}
                  className="bg-slate-50 border border-slate-200 p-3 rounded-lg flex flex-col gap-3 md:flex-row md:justify-between md:items-center"
                >
                  <div className="flex flex-col">
                    <span className="break-all font-mono text-sm text-slate-800">
                      {key.masked_key || key.key}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {new Date(key.created_at).toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={() => deleteApiKey(key.id)}
                    disabled={!canUseApiAccess}
                    className={secondaryButtonClass}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              ))}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
              No API keys created yet.
            </div>
          )}

          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Example Request</h3>
          <pre className="overflow-x-auto bg-slate-950 text-emerald-300 p-4 rounded-lg text-sm">
{`curl -X POST https://yourapi.com/api/v1/cv/analyze \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-F "cv_file=@resume.pdf" \\
-F "job_name=Frontend Engineer" \\
-F "job_description=Frontend Engineer" \\
-F "include_text=false"`}
          </pre>
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            API responses include score, matching skills, gaps, top qualification, AI summary,
            AI strengths, AI gaps, skill evidence, and interview questions. Full CV text is hidden
            unless you explicitly send <span className="font-mono">include_text=true</span>. Bulk API
            analyze requests accept up to 25 CVs per call.
          </div>
          </div>
        </div>
      )}

      {activeTab === "history" && (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">API Usage Summary</h3>
              <p className="text-sm text-slate-500">A compact view of recent external API activity.</p>
            </div>
            <button
              onClick={fetchApiHistory}
              className={secondaryButtonClass}
              disabled={!canUseApiAccess || loadingHistory}
            >
              <Activity size={16} />
              {loadingHistory ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            {[
              ["Total calls", summary.total_calls || 0, "bg-slate-50 text-slate-800"],
              ["Successful", summary.successful_calls || 0, "bg-emerald-50 text-emerald-800"],
              ["Failed", summary.failed_calls || 0, "bg-rose-50 text-rose-800"],
              ["Success rate", `${summary.success_rate || 0}%`, "bg-sky-50 text-sky-800"],
            ].map(([label, value, tone]) => (
              <div key={label} className={`rounded-lg border border-slate-200 p-4 ${tone}`}>
                <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
                <p className="mt-2 text-2xl font-bold">{value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h4 className="font-semibold text-slate-900">Rate Limits</h4>
            <p className="mt-1 text-sm text-slate-500">
              These limits protect your account from unexpected API and AI usage costs.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {[
                ["Per minute", rateLimits.per_minute],
                ["Per hour", rateLimits.per_hour],
                ["Per day", rateLimits.per_day],
              ].map(([label, value]) => (
                <div key={label} className="rounded-md bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{value ?? "-"}</p>
                </div>
              ))}
            </div>
          </div>

          {summary.endpoint_counts?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h4 className="font-semibold text-slate-900">Endpoint Breakdown</h4>
              <div className="mt-3 space-y-2">
                {summary.endpoint_counts.map((item) => (
                  <div key={item.endpoint} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-mono text-slate-700">{item.endpoint}</span>
                    <span className="font-semibold text-slate-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <h4 className="font-semibold text-slate-900">Recent Calls</h4>
            </div>
            {recentCalls.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Endpoint</th>
                      <th className="px-4 py-3 font-semibold">Files</th>
                      <th className="px-4 py-3 font-semibold">Scans</th>
                      <th className="px-4 py-3 font-semibold">Duration</th>
                      <th className="px-4 py-3 font-semibold">API Key</th>
                      <th className="px-4 py-3 font-semibold">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recentCalls.map((call) => (
                      <React.Fragment key={call.id}>
                        <tr>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                              call.success ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                            }`}>
                              {call.success ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                              {call.status_code}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-slate-700">{call.method} {call.endpoint}</td>
                          <td className="px-4 py-3 text-slate-600">{call.file_count ?? 0}</td>
                          <td className="px-4 py-3 text-slate-600">{call.scan_count ?? 0}</td>
                          <td className="px-4 py-3 text-slate-600">
                            {call.duration_ms ? `${call.duration_ms} ms` : "-"}
                          </td>
                          <td className="px-4 py-3 font-mono text-slate-500">{call.api_key}</td>
                          <td className="px-4 py-3 text-slate-500">
                            {call.created_at ? new Date(call.created_at).toLocaleString() : "-"}
                          </td>
                        </tr>
                        {call.error_message && (
                          <tr className="bg-rose-50/60">
                            <td colSpan={7} className="px-4 py-2 text-xs text-rose-700">
                              {call.error_message}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-5 text-sm text-slate-500">
                {loadingHistory ? "Loading API history..." : "No API calls logged yet."}
              </div>
            )}
          </div>
        </div>
      )}
        </ToolBody>
      </ToolPage>
    </motion.div>
  );
}
